import {Component, HostBinding, HostListener, OnDestroy, OnInit} from '@angular/core';
import { Router } from '@angular/router';

import { Subscription } from 'rxjs';
import { ReportPageConstant } from 'src/app/constants/reportPage.constants';
import { ReportPageInterface } from 'src/app/interface';
import { BalanceSheet, FieldGroupValue, FieldList, FieldValue, FieldValueReset, Report } from 'src/app/model';
import { BusinessPurpose, FooterButtonOption, PageNumbers, TransactionType } from 'src/app/model/Enums';
import { CurrencyPipe } from 'src/app/pipes';
import { ReportProvider } from 'src/app/providers';
import { ReportService, ToasterService } from 'src/app/services';
import {routeTransition} from '../../../../animations/route-transition';

@Component({
  selector: 'app-assets-liabilities',
  templateUrl: './assets-liabilities.component.html',
  styleUrls: ['./assets-liabilities.component.scss'],
  animations: [routeTransition],
})
export class AssetsLiabilitiesComponent implements OnInit, OnDestroy, ReportPageInterface {
  @HostBinding('@routeTransition') routeAnimation = true;
  public currentReport: Report;
  private reportSub: Subscription;
  reportPageConst = ReportPageConstant;

  isCheckBoxDisabled: boolean = true;
  isDebtRefinance: boolean = false;

  constructor(public reportService: ReportService,
    private reportProvider: ReportProvider,
    private router: Router,
    public currencyPipe: CurrencyPipe,
    private toastr: ToasterService) { }

  ngOnInit(): void {
    this.reportSub = this.reportService.report$.subscribe(currentReport => {
      this.currentReport = currentReport;
      if (this.currentReport?.LATEST && !this.currentReport.LATEST.BALANCE_SHEET) {
        this.createNewBalanceSheetObject();
      }
      if (this.currentReport?.LATEST?.COMPANY) {
        this.isCheckBoxDisabled = this.currentReport.LATEST.COMPANY.TRANSACTION_TYPE != TransactionType.CHANGE_OWNERSHIP && this.currentReport.LATEST.COMPANY.BUSINESS_PURPOSE != BusinessPurpose.PURCHASE_OR_SALE_BUSINESS;
        this.isDebtRefinance = this.currentReport.LATEST.COMPANY.TRANSACTION_TYPE == TransactionType.REFINANCING;
      }
    });
  }

  ngOnDestroy(): void {
    if (this.reportSub)
      this.reportSub.unsubscribe();
  }

  /**
   * @description for create new balance sheet
   */
  createNewBalanceSheetObject() {
    this.reportProvider.saveBalanceSheet(this.currentReport.LATEST.ID, new BalanceSheet()).subscribe(resp => {
      if (resp.success) {
        this.currentReport.LATEST.BALANCE_SHEET = resp.result;
      }
    }, err => {
      // Error Handling
    });
  }

  /**
   * @description field update API
   * @param fieldName field name which we want to update
   */
  callUpdateAPI(fieldName: string) {

    setTimeout(() => {
      this.autoScroll(fieldName);
    }, 10);

    let objFieldValue = new FieldValue();
    objFieldValue.GROUP = 'BALANCE_SHEET';
    objFieldValue.FIELD = fieldName;
    objFieldValue.VALUE = this.currentReport.LATEST.BALANCE_SHEET[fieldName];
    this.reportProvider.updateReportField(this.currentReport.LATEST.ID, objFieldValue).subscribe(res => {
      if (!res.success) {
        this.toastr.error(res.error.txt);
      }
    }, err => {
      // Error Handling
    });
  }

  autoScroll(fieldName: string) {
    switch (fieldName) {
      case 'ASSET_CASH':
        this.reportService.makeElementFocus('ASSET_AR');
        break;
      case 'ASSET_AR':
        this.reportService.makeElementFocus('ASSET_INVENTORY');
        break;
      case 'ASSET_INVENTORY':
        this.reportService.makeElementFocus('ASSET_FIXED_COST');
        break;
      case 'ASSET_FIXED_COST':
        this.reportService.makeElementFocus('ASSET_REAL_ESTATE');
        break;
      case 'ASSET_REAL_ESTATE':
        this.reportService.makeElementFocus('assets-lib-asset-balance-sheet');
        break;
      case 'ASSET_OTHER_LABEL':
        this.reportService.makeElementFocus('ASSET_OTHER');
        break;
      case 'ASSET_OTHER':
        this.reportService.makeElementFocus('LIABILITY_AP');
        break;
      case 'LIABILITY_AP':
        this.reportService.makeElementFocus('LIABILITY_SHORT_TERM_NOTES');
        break;
      case 'LIABILITY_SHORT_TERM_NOTES':
        this.reportService.makeElementFocus('LIABILITY_LONG_TERM');
        break;
      case 'LIABILITY_LONG_TERM':
        this.reportService.makeElementFocus('assets-lib-other-label');
        break;
      case 'LIABILITY_OTHER_LABEL':
        this.reportService.makeElementFocus('LIABILITY_OTHER');
        break;
    }
  }

  /**
   * @description for update group of fields
   */
  callUpdateGroupAPI() {
    let objFieldArray = new Array<FieldList>();
    objFieldArray.push({ FIELD: 'ACQ_NONE', VALUE: this.currentReport.LATEST.BALANCE_SHEET.ACQ_NONE });
    objFieldArray.push({ FIELD: 'ACQ_PRICE', VALUE: this.currentReport.LATEST.BALANCE_SHEET.ACQ_PRICE });

    let objFieldGroup = new FieldGroupValue();
    objFieldGroup.GROUP = 'BALANCE_SHEET';
    objFieldGroup.FIELD_LIST = objFieldArray;

    this.reportProvider.updateReportFieldGroup(this.currentReport.LATEST.ID, objFieldGroup).subscribe(res => {
      if (!res.success) {
        this.toastr.error(res.error.txt);
      }
    }, err => {
      // Error Handling
    });
  }

  /**
   * @description field update API
   * @param fieldName field name which we want to update
   */
  callResetOtherAPI(fieldList: Array<string> = []) {
    let objFieldValue = new FieldValueReset();
    objFieldValue.GROUP = 'BALANCE_SHEET';
    objFieldValue.FIELD_LIST = fieldList;
    this.reportProvider.resetReportField(this.currentReport.LATEST.ID, objFieldValue).subscribe(res => {
      if (!res.success) {
        this.toastr.error(res.error.txt);
      }
    }, err => {
      // Error Handling
    });
  }

  /**
   * @description goto Transaction Type
   */
  gotoTransactionType() {
    // comment for alternative flow
    // this.reportService.updateMaxStage(PageNumbers.PURCHASE_PRICE);
    // this.router.navigateByUrl(`/report/${this.currentReport.ID}/overview/transaction-type`);
    this.reportService.updateMaxStage(PageNumbers.ASSETS_LIABILITIES);
    this.router.navigateByUrl(`/report/${this.currentReport.ID}/overview`);
  }

  /**
   * @description for scroll focuse on inputs
   * @param $event key down
   */
  @HostListener('window:keyup', ['$event'])
  onKeyPress($event: KeyboardEvent) {
    if ($event.keyCode == 9) {
      const elePosition = document.activeElement.getBoundingClientRect();
      if (window.innerHeight - 115 < elePosition.y) {
        document.activeElement.scrollIntoView(true);
      }
    }
  }

  /**
   * @description function of interface for validation
   * @returns form valid or not
   */
  isFormValid(): boolean {
    return true;
  }

  /**
   * @description function of interface for skip functionality
   * @returns form can be skippable or not
   */
  canBeSkipped(): boolean {
    return true;
  }

  /**
   * @description report footer button click options
   * @param buttonOption which button clicked by user
   */
  footerButtonClick(buttonOption: number) {
    if (buttonOption == FooterButtonOption.NEXT_CLICK) {
      // comment for alternative flow
      // this.reportService.updateMaxStage(PageNumbers.PURCHASE_PRICE);
      // this.router.navigateByUrl(`/report/${this.currentReport.ID}/assetsLiabilities/assets`);
      this.reportService.updateMaxStage(PageNumbers.ASSETS_LIABILITIES);
      this.router.navigateByUrl(`/report/${this.currentReport.ID}/risk/diversification`);
    } else if (buttonOption == FooterButtonOption.BACK_CLICK) {
      if (this.reportService.showWeighting()) {
        this.router.navigateByUrl(`/report/${this.currentReport.ID}/weighting/weighting-table`);
      } else {
        this.router.navigateByUrl(`/report/${this.currentReport.ID}/projections/projection-table`);
      }
    }
  }

}
